import React from 'react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-saverly-primary/10 to-saverly-deep/10 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-saverly-dark font-poppins mb-6">
            Privacy Policy
          </h1>
          <p className="text-lg text-saverly-gray font-inter">
            Last Updated: December 7, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <p className="text-saverly-gray font-inter leading-relaxed mb-4">
              Saverly ("Saverly," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, share, and safeguard your information when you use our mobile application, website, and related services (collectively, the "Services").
            </p>
            <p className="text-saverly-gray font-inter leading-relaxed mb-12">
              By using Saverly, you agree to this Privacy Policy. If you do not agree, please discontinue use of the Services.
            </p>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">1.1 Personal Information You Provide</h3>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                When you create an account or use premium features, we may collect:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Name and email address</li>
                <li>Dietary and cuisine preferences, allergies, family size</li>
                <li>Store preferences and product interests</li>
                <li>Saved meal plans and shopping lists</li>
                <li>Subscription and billing-related details (managed through Clerk)</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed">
                <strong>We do not directly collect or store your full payment card data.</strong> All billing information is handled through Clerk and its associated payment processor partners.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">2. How We Use Your Information</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We use your information to:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 ml-4">
                <li>Authenticate your account</li>
                <li>Provide personalized, AI-generated meal plans</li>
                <li>Match ingredients to local store products</li>
                <li>Manage your subscription and billing</li>
                <li>Display relevant sales, pricing, and product data</li>
                <li>Maintain and enhance the performance of the Services</li>
                <li>Communicate updates, feature releases, and support messages</li>
                <li>Ensure the security and integrity of the platform</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">3. Payments, Subscriptions, and Billing (Clerk)</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Saverly uses Clerk to manage:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>User authentication</li>
                <li>Subscription status</li>
                <li>Payment processing</li>
                <li>Billing history</li>
                <li>Payment method updates</li>
                <li>Trial management</li>
                <li>Invoice and receipt delivery</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Clerk may collect identifiable billing data such as:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Payment method details (tokenized)</li>
                <li>Billing address (if required by payment compliance)</li>
                <li>Transaction metadata</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                <strong>Saverly never processes, stores, or has access to your full payment card number or CVV.</strong> That information is handled directly by Clerk and its PCI-compliant payment partners.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                For details on Clerk's data handling, please review Clerk's own Privacy Policy.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">4. AI and Data Processing (OpenAI)</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                When generating a meal plan or recipe:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>We send your dietary preferences and selected inputs to OpenAI</li>
                <li>We do not send your name, email, or billing information</li>
                <li>OpenAI processes the data solely to return the requested output</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed">
                No personally identifying data is shared beyond what is necessary to generate meal content.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">5. How We Share Your Information</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                <strong>We do not sell your personal information.</strong>
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We share information only in these cases:
              </p>
              
              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">5.1 Service Providers</h3>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We work with trusted third-party processors, including:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Clerk – authentication, identity management, subscription and billing processing</li>
                <li>Convex – database and backend operations</li>
                <li>OpenAI – meal plan and recipe generation</li>
                <li>Kroger – product data retrieval (no personal habits shared unless authorized)</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                These providers receive only the minimal data required to perform their functions.
              </p>

              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">5.2 Legal Compliance</h3>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We may disclose information as required by law or government request.
              </p>

              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">5.3 Business Transfers</h3>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Your data may be transferred in a merger, acquisition, or asset sale.
              </p>

              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">5.4 With Your Consent</h3>
              <p className="text-saverly-gray font-inter leading-relaxed">
                For purposes outside this policy, we will request explicit permission.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">6. Data Security</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We use strong security controls, including:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Encrypted connections (TLS/HTTPS)</li>
                <li>Tokenized authentication and payment flows (via Clerk)</li>
                <li>Access-controlled data storage (via Convex)</li>
                <li>Least-privilege authorization models</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed">
                Despite safeguards, no system can guarantee absolute security.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">7. Data Retention</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We retain user data for as long as necessary to:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Provide the Services</li>
                <li>Maintain your account</li>
                <li>Meet legal, tax, and compliance obligations</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed">
                Upon account deletion, personal data is removed or anonymized unless legally required to be retained.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">8. Your Privacy Rights</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                You may have rights such as:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Access to your personal data</li>
                <li>Correction of inaccuracies</li>
                <li>Deletion of your data</li>
                <li>Restriction or objection to processing</li>
                <li>Exporting your data ("portability")</li>
                <li>Opting out of marketing</li>
                <li>Withdrawing consent</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Requests can be made at: <a href="mailto:privacy@saverly.com" className="text-saverly-primary hover:text-saverly-deep underline">privacy@saverly.com</a>
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                We will verify your identity before processing requests.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">9. Cookies and Tracking</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Saverly uses cookies and similar technologies for:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Session management</li>
                <li>User preferences</li>
                <li>Analytics</li>
                <li>Feature performance monitoring</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed">
                You may disable cookies, but some features may not function properly.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">10. Children's Privacy</h2>
              <p className="text-saverly-gray font-inter leading-relaxed">
                Saverly is not intended for children under 13. We do not knowingly collect data from children under 13, and will delete such data if discovered.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">11. International Users</h2>
              <p className="text-saverly-gray font-inter leading-relaxed">
                If you access Saverly from outside the United States, your information may be processed in the U.S. where privacy laws may differ.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">12. Changes to This Policy</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We may update this Privacy Policy. Changes become effective when posted with an updated "Last Updated" date.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                Continued use of Saverly constitutes acceptance.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">13. Contact Us</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                For privacy concerns or rights requests:
              </p>
              <ul className="list-none text-saverly-gray font-inter space-y-2">
                <li><strong>Email:</strong> <a href="mailto:privacy@saverly.com" className="text-saverly-primary hover:text-saverly-deep underline">privacy@saverly.com</a></li>
                <li><strong>Email:</strong> <a href="mailto:support@saverly.com" className="text-saverly-primary hover:text-saverly-deep underline">support@saverly.com</a></li>
              </ul>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;

